import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '@/core/services/ApiService';

export interface IBottleStatusMeta {
  _id?: string;
  key: string;
  label: string;
  label_th: string;
  description_th?: string;
  color: string;
  hex?: string;
  sort_order?: number;
  is_default?: boolean;
  status?: 'active' | 'inactive';
  show_in_mobile?: boolean;
  usage?: { used_count?: number };
  can_delete?: boolean;
  blocked_reason?: string | null;
}

const FALLBACK_META = (key: string): IBottleStatusMeta => ({
  key,
  label: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
  label_th: key,
  color: 'secondary',
});

/**
 * Status enums fetched from the API (single source of truth from status.config.ts).
 * Call `load()` once during app bootstrap.
 */
export const useBottleStatusStore = defineStore('bottle-status', () => {
  const bottleStatuses = ref<IBottleStatusMeta[]>([]);
  const loaded = ref(false);
  let loadPromise: Promise<void> | null = null;

  const bottleStatusMap = computed<Record<string, IBottleStatusMeta>>(() =>
    Object.fromEntries(bottleStatuses.value.map(s => [s.key, s]))
  );

  const bottleStatusKeys = computed<string[]>(() => bottleStatuses.value.map(s => s.key));

  function getBottleStatusMeta(key: string): IBottleStatusMeta {
    return bottleStatusMap.value[key] ?? FALLBACK_META(key);
  }

  function formatBottleStatus(key: string): string {
    const meta = bottleStatusMap.value[key];
    if (!meta) return FALLBACK_META(key).label;
    return `${meta.label_th} (${meta.label})`;
  }

  function formatBottleStatusEN(key: string): string {
    return getBottleStatusMeta(key).label;
  }

  function getBottleStatusBadgeClass(key: string): string {
    return `badge badge-light-${getBottleStatusMeta(key).color || 'secondary'}`;
  }

  async function load(force = false): Promise<void> {
    if (loaded.value && !force) return;
    if (loadPromise) return loadPromise;
    loadPromise = (async () => {
      try {
        const res = await ApiService.get('/api/v1/wine-bottles/statuslists');
        const data = (res?.data?.data ?? res?.data) as IBottleStatusMeta[];
        if (Array.isArray(data)) {
          bottleStatuses.value = data;
          loaded.value = true;
        }
      } catch (err) {
        console.error('[status] failed to load bottle statuses', err);
      } finally {
        loadPromise = null;
      }
    })();
    return loadPromise;
  }

  function reset() {
    bottleStatuses.value = [];
    loaded.value = false;
    loadPromise = null;
  }

  return {
    bottleStatuses,
    bottleStatusMap,
    bottleStatusKeys,
    loaded,
    load,
    reset,
    getBottleStatusMeta,
    formatBottleStatus,
    formatBottleStatusEN,
    getBottleStatusBadgeClass,
  };
});
